import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FileSpreadsheet, BadgeCheck, Zap, BarChart3,
  TrendingUp, Leaf, AlertTriangle, ArrowRight, Loader2, Copy, Check as CheckIcon,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import Navbar from "../components/layout/Navbar";
import { useAuth } from "../hooks/useAuth";
import { getAccountMe, startFreeTrial } from "../api/client";
import { SkeletonStatCard, Skeleton, SkeletonCard, SkeletonTextBlock } from "../components/ui/Skeleton";
import "../styles/dashboard.css";

// ── Score badge ───────────────────────────────────────────────────────────────
function ScoreBadge({ score }) {
  if (score == null) return <span className="gs-score-badge mid">—</span>;
  const cls = score >= 70 ? "high" : score >= 40 ? "mid" : "low";
  return <span className={`gs-score-badge ${cls}`}>{score}</span>;
}

// ── Cert chip ─────────────────────────────────────────────────────────────────
function CertChip({ status }) {
  if (!status) return <span className="gs-cert-chip none">None</span>;
  const cls = status === "ACTIVE" ? "issued" : "revoked";
  return <span className={`gs-cert-chip ${cls}`}>{status}</span>;
}

// ── Subscription card ─────────────────────────────────────────────────────────
function SubscriptionCard({ subscription, onTrialActivated }) {
  const [activating, setActivating] = useState(false);
  const [trialError, setTrialError] = useState("");
  const [copied, setCopied] = useState(false);

  async function handleActivateTrial() {
    setActivating(true);
    setTrialError("");
    try {
      const res = await startFreeTrial();
      onTrialActivated?.(res.data);
      toast.success("Free trial activated! You have 2 screening runs.");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to activate trial.";
      setTrialError(msg);
      toast.error(msg);
    } finally {
      setActivating(false);
    }
  }

  function copyCode(code) {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Access code copied!");
    });
  }

  if (!subscription) {
    return (
      <div className="gs-sub-card no-sub">
        <div className="gs-sub-badge">No active plan</div>
        <p className="gs-sub-plan">Start free</p>
        <p className="gs-sub-meta">
          Activate your free trial — 2 screening runs, no credit card required.
        </p>
        {trialError && (
          <p style={{ fontSize: "0.78rem", color: "#fca5a5", marginBottom: "0.75rem" }}>
            {trialError}
          </p>
        )}
        <button className="gs-sub-cta" onClick={handleActivateTrial} disabled={activating}>
          {activating
            ? <><Loader2 size={14} className="spin" /> Activating…</>
            : <>Activate Free Trial <ArrowRight size={14} /></>
          }
        </button>
      </div>
    );
  }

  const { plan_type, max_analyses, used_count, remaining, expiry_date, code } = subscription;
  const isUnlimited = max_analyses >= 9999;
  const usedPct     = isUnlimited ? 0 : Math.min(100, Math.round((used_count / max_analyses) * 100));
  const expiryFmt   = expiry_date
    ? new Date(expiry_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "—";
  const planLabel   = plan_type.replace(/_/g, " ");

  return (
    <div className="gs-sub-card">
      <div className="gs-sub-badge">{planLabel}</div>
      <p className="gs-sub-plan">
        {isUnlimited ? "Unlimited" : `${remaining} run${remaining !== 1 ? "s" : ""} left`}
      </p>
      <p className="gs-sub-meta" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <strong style={{ fontFamily: "var(--ft-mono)", fontSize: "0.85rem" }}>{code}</strong>
        <button
          onClick={() => copyCode(code)}
          style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.7)", padding: 0, display: "flex", alignItems: "center" }}
          title="Copy code"
        >
          {copied ? <CheckIcon size={13} /> : <Copy size={13} />}
        </button>
      </p>

      {!isUnlimited && (
        <>
          <div className="gs-sub-usage-bar-wrap">
            <div className="gs-sub-usage-bar" style={{ width: `${usedPct}%` }} />
          </div>
          <div className="gs-sub-usage-label">
            <span>{used_count} used</span>
            <span>{max_analyses} total</span>
          </div>
        </>
      )}

      <p className="gs-sub-expiry">Expires {expiryFmt}</p>

      <Link to="/#access" className="gs-sub-cta">
        Upgrade plan <ArrowRight size={14} />
      </Link>
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, value, label, delta, color }) {
  return (
    <div className="gs-stat-card">
      <div className="gs-stat-icon" style={{ background: `${color}15` }}>
        <Icon size={18} color={color} />
      </div>
      <div className="gs-stat-value">{value}</div>
      <div className="gs-stat-label">{label}</div>
      {delta && <div className="gs-stat-delta">{delta}</div>}
    </div>
  );
}

// ── Quick action card ──────────────────────────────────────────────────────────
function QuickCard({ icon: Icon, title, description, to, label, color, disabled }) {
  const inner = (
    <>
      <div className="gs-quick-icon" style={{ background: `${color}15` }}>
        <Icon size={20} color={color} />
      </div>
      <p className="gs-quick-title">{title}</p>
      <p className="gs-quick-desc">{description}</p>
      <span className="gs-quick-label" style={{ color }}>{label} →</span>
    </>
  );

  if (disabled) {
    return (
      <div className="gs-quick-card" style={{ opacity: 0.55, cursor: "default" }}>
        {inner}
      </div>
    );
  }

  return (
    <Link to={to} className="gs-quick-card">
      {inner}
    </Link>
  );
}

// ── Recent runs table ─────────────────────────────────────────────────────────
function RecentRunsCard({ runs }) {
  return (
    <div className="gs-card">
      <div className="gs-card-header">
        <h3 className="gs-card-title">Recent screening runs</h3>
        <Link to="/screening" className="gs-card-link">Run new →</Link>
      </div>

      {runs.length === 0 ? (
        <div className="gs-empty">
          No screening runs yet. Upload a GST CSV to get started.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="gs-runs-table">
            <thead>
              <tr>
                <th>GSTIN</th>
                <th>Score</th>
                <th>Status</th>
                <th>Carbon (kg)</th>
                <th>Certificate</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td><span className="gs-gstin-code">{run.gstin}</span></td>
                  <td><ScoreBadge score={run.green_score} /></td>
                  <td style={{ fontSize: "0.78rem", color: "#64748b" }}>
                    {run.transition_status || "—"}
                  </td>
                  <td style={{ fontSize: "0.82rem" }}>
                    {run.total_carbon > 0 ? run.total_carbon.toFixed(2) : "—"}
                  </td>
                  <td><CertChip status={run.cert_status} /></td>
                  <td style={{ fontSize: "0.75rem", color: "#94a3b8", whiteSpace: "nowrap" }}>
                    {run.created_at
                      ? new Date(run.created_at).toLocaleDateString("en-IN", {
                          day: "numeric", month: "short", year: "numeric",
                        })
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const { accountId } = useAuth();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback((showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    return getAccountMe()
      .then((res) => { setData(res.data); setError(""); })
      .catch((err) => setError(err.response?.data?.detail || "Failed to load dashboard."))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  function handleTrialActivated() {
    fetchData();
  }

  // Initial load
  useEffect(() => { fetchData(); }, [fetchData]);

  // Refresh when user returns to this tab (e.g. after doing a screening)
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") fetchData(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchData]);

  // Auto-refresh every 90 seconds while the tab is visible
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") fetchData();
    }, 90_000);
    return () => clearInterval(timer);
  }, [fetchData]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="gs-dash">
      <Navbar />

      <main className="gs-dash-main">
        {/* Header */}
        <div className="gs-dash-header">
          <div>
            <h1 className="gs-dash-greeting">{greeting}</h1>
            <p className="gs-dash-sub">
              {data?.subscription ? (
                <>
                  <span className="gs-usage-pill">
                    {data.subscription.used_count} / {data.subscription.max_analyses} runs used
                    &nbsp;&middot;&nbsp;
                    <strong>{data.subscription.remaining} remaining</strong>
                    &nbsp;&middot;&nbsp;{data.subscription.plan_type}
                  </span>
                </>
              ) : (
                <>Account&nbsp;
                  <code style={{ background: "#f1f5f9", padding: "0.15rem 0.4rem", borderRadius: "4px", fontSize: "0.78rem" }}>
                    {accountId}
                  </code>
                </>
              )}
            </p>
          </div>
          <button
            className="gs-refresh-btn"
            onClick={() => fetchData(true)}
            disabled={refreshing || loading}
            title="Refresh dashboard"
          >
            <RefreshCw size={15} className={refreshing ? "gs-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {loading && (
          <>
            <p className="gs-dash-section-title">Overview</p>
            <div className="gs-dash-skeleton-stats">
              {[...Array(5)].map((_, i) => <SkeletonStatCard key={i} />)}
            </div>
            <p className="gs-dash-section-title">Subscription &amp; activity</p>
            <div className="gs-dash-skeleton-grid">
              <SkeletonCard style={{ minHeight: 220 }}>
                <Skeleton width="50%" height={16} radius={4} />
                <Skeleton width="70%" height={32} radius={6} style={{ marginTop: "0.75rem" }} />
                <SkeletonTextBlock lines={3} style={{ marginTop: "1rem" }} />
              </SkeletonCard>
              <SkeletonCard style={{ minHeight: 220 }}>
                <Skeleton width="40%" height={16} radius={4} />
                <SkeletonTextBlock lines={5} style={{ marginTop: "1rem" }} />
              </SkeletonCard>
            </div>
          </>
        )}

        {error && !loading && (
          <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "10px", padding: "1rem 1.25rem", color: "#991b1b", fontSize: "0.85rem", marginBottom: "1.5rem" }}>
            <AlertTriangle size={16} style={{ display: "inline", marginRight: "0.4rem", verticalAlign: "middle" }} />
            {error}
          </div>
        )}

        {data && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Stats */}
            <p className="gs-dash-section-title">Overview</p>
            <div className="gs-dash-stats">
              {[
                { icon: FileSpreadsheet, value: data.stats.total_uploads,    label: "CSV uploads",      color: "#1a6b3c" },
                { icon: BarChart3,       value: data.stats.total_score_runs, label: "Score runs",       color: "#0d9488", delta: data.stats.runs_last_30d > 0 ? `+${data.stats.runs_last_30d} this month` : null },
                { icon: Leaf,            value: data.stats.unique_gstins,    label: "Unique GSTINs",    color: "#059669" },
                { icon: BadgeCheck,      value: data.stats.certs_issued,     label: "Certificates",     color: "#f59e0b" },
                { icon: TrendingUp,      value: data.stats.avg_score || "—", label: "Avg green score",  color: "#8b5cf6" },
              ].map(({ icon, value, label, color, delta }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, delay: i * 0.06, ease: "easeOut" }}
                >
                  <StatCard icon={icon} value={value} label={label} color={color} delta={delta} />
                </motion.div>
              ))}
            </div>

            {/* Subscription + Recent runs */}
            <p className="gs-dash-section-title">Subscription &amp; activity</p>
            <div className="gs-dash-grid">
              <SubscriptionCard subscription={data.subscription} onTrialActivated={handleTrialActivated} />
              <RecentRunsCard runs={data.recent_runs} />
            </div>

            {/* Quick actions */}
            <p className="gs-dash-section-title">Quick actions</p>
            <div className="gs-quick-grid">
              <QuickCard
                icon={FileSpreadsheet}
                title="New screening run"
                description="Upload a GST CSV to score supplier carbon exposure across any GSTIN."
                to="/screening"
                label="Start screening"
                color="#1a6b3c"
              />
              <QuickCard
                icon={BadgeCheck}
                title="Issue certificate"
                description="Generate a verifiable Green Score certificate for a passed screening."
                to="/screening"
                label="Go to console"
                color="#0d9488"
              />
              <QuickCard
                icon={Zap}
                title="Auto GST fetch"
                description="Pull invoice data directly from the GST portal. Joining waitlist now."
                to="/screening"
                label="Join waitlist"
                color="#8b5cf6"
                disabled
              />
              <QuickCard
                icon={TrendingUp}
                title="Upgrade plan"
                description="Get more screening runs, API access, and branded PDF reports."
                to="/#access"
                label="View plans"
                color="#f59e0b"
              />
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
